import { useState, useEffect, useRef, useMemo } from 'react';
import { Auth } from './components/Auth';
import { Profile } from './components/Profile';
import { QRScanner } from './components/QRScanner';
import { QRCodeForm } from './components/QRCodeForm';
import { FolderManager } from './components/FolderManager';
import { supabase } from './lib/supabase';
import type { User } from '@supabase/supabase-js';
import type { Database } from './lib/database.types';
import { downloadCSV } from './utils/csv';
import { parseVCardData } from './utils/vcard';
import { createVCard } from './utils/vcard';

const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
  e.preventDefault();
  e.returnValue = '';
  return '';
};

type ScannedQRCode = Database['public']['Tables']['scanned_qr_codes']['Row'];
type QRCodeData = Database['public']['Tables']['qr_codes']['Row'];
type Folder = Database['public']['Tables']['folders']['Row'];

type Tab = 'qrcodes' | 'qrmode' | 'create' | 'scan' | 'scanned' | 'profile';

function App() {
  useEffect(() => {
    window.addEventListener('beforeunload', beforeUnloadHandler);

    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    };
  }, []);

  const [user, setUser] = useState<User | null>(null);
  const [qrCodes, setQrCodes] = useState<QRCodeData[]>([]);
  const [scannedQRCodes, setScannedQRCodes] = useState<ScannedQRCode[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('qrcodes');
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [editingQRCode, setEditingQRCode] = useState<QRCodeData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [userSettings, setUserSettings] = useState<{
    name: string;
    logo: string;
    photo: string;
  }>({
    name: '',
    logo: '',
    photo: ''
  });
  const [currentQRIndex, setCurrentQRIndex] = useState(0);

  const carouselRef = useRef<HTMLDivElement>(null);

  const scanStats = useMemo(() => {
    const total = scannedQRCodes.length;
    const leads = scannedQRCodes.filter(qr => qr.lead_type === 'business_card').length;
    const links = scannedQRCodes.filter(qr => qr.lead_type === 'link').length;
    return { total, leads, links };
  }, [scannedQRCodes]);

  const filteredQRCodes = useMemo(() => {
    if (selectedFolder === null) {
      return qrCodes;
    }
    return qrCodes.filter(qr => qr.folder_id === selectedFolder);
  }, [qrCodes, selectedFolder]);

  useEffect(() => {
    initializeApp();
  }, []);

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel || activeTab !== 'qrmode') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            setCurrentQRIndex(index);
          }
        });
      },
      {
        root: carousel,
        threshold: 0.7,
        rootMargin: '0px'
      }
    );

    const cards = carousel.querySelectorAll('.qr-card');
    cards.forEach(card => observer.observe(card));

    return () => {
      cards.forEach(card => observer.unobserve(card));
    };
  }, [activeTab, filteredQRCodes]);

  const initializeApp = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      if (user) {
        await Promise.all([
          loadQRCodes(),
          loadScannedQRCodes(),
          loadUserSettings(),
          loadFolders()
        ]);
      }
    } catch (error) {
      console.error('Error initializing app:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setUserSettings({
          name: data.name || '',
          logo: data.logo || '',
          photo: data.photo || ''
        });
        setDarkMode(data.dark_mode || false);
      }
    } catch (error) {
      console.error('Error loading user settings:', error);
    }
  };

  const loadFolders = async () => {
    try {
      const { data, error } = await supabase
        .from('folders')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      setFolders(data || []);
    } catch (error) {
      console.error('Error loading folders:', error);
    }
  };

  const loadQRCodes = async () => {
    try {
      const { data, error } = await supabase
        .from('qr_codes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const sortedData = (data || []).sort((a, b) => {
        if (a.affiliate_enabled) return -1;
        if (b.affiliate_enabled) return 1;
        
        if (a.is_business_card && !b.is_business_card) return -1;
        if (!a.is_business_card && b.is_business_card) return 1;
        
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      });

      setQrCodes(sortedData);
    } catch (error) {
      console.error('Error loading QR codes:', error);
      setError('Failed to load QR codes');
    }
  };

  const loadScannedQRCodes = async () => {
    try {
      const { data, error } = await supabase
        .from('scanned_qr_codes')
        .select('*')
        .order('scanned_at', { ascending: false });

      if (error) throw error;
      setScannedQRCodes(data || []);
    } catch (error) {
      console.error('Error loading scanned QR codes:', error);
    }
  };

  const handleScanSuccess = async (decodedText: string) => {
    try {
      if (user) {
        let leadType: 'business_card' | 'link' | null = null;
        let interpretedData = null;

        if (decodedText.startsWith('BEGIN:VCARD')) {
          leadType = 'business_card';
          interpretedData = parseVCardData(decodedText);
        } else if (decodedText.startsWith('http')) {
          leadType = 'link';
        }

        const newScannedQRCode = {
          user_id: user.id,
          title: leadType === 'business_card' 
            ? `Business Card - ${new Date().toLocaleString()}`
            : `Link - ${new Date().toLocaleString()}`,
          raw_data: decodedText,
          interpreted_data: interpretedData,
          lead_type: leadType
        };

        const { error } = await supabase
          .from('scanned_qr_codes')
          .insert([newScannedQRCode]);

        if (error) throw error;

        await loadScannedQRCodes();
      }

      setActiveTab('scanned');
    } catch (error) {
      console.error('Error saving scanned QR code:', error);
    }
  };

  const handleDeleteScannedQR = async (id: string) => {
    try {
      const { error } = await supabase
        .from('scanned_qr_codes')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await loadScannedQRCodes();
    } catch (error) {
      console.error('Error deleting scanned QR code:', error);
    }
  };

  const handleExportCSV = () => {
    const headers = {
      title: 'Title',
      raw_data: 'Data',
      scanned_at: 'Scanned At',
      lead_type: 'Type'
    };

    downloadCSV(scannedQRCodes, {
      filename: 'scanned-qr-codes.csv',
      headers,
      dateFields: ['scanned_at', 'created_at']
    });
  };

  const handleSaveToContacts = async (contact: any) => {
    try {
      // Create vCard data
      const vCardData = createVCard(contact);

      // Try native sharing on mobile devices first
      if (navigator.share && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        try {
          const file = new File([vCardData], `${contact.name}.vcf`, {
            type: 'text/vcard'
          });

          await navigator.share({
            files: [file]
          });
          setError(null);
          return true;
        } catch (shareError) {
          console.log('Share failed, falling back to direct download:', shareError);
          // Fall through to direct download if sharing fails
        }
      }

      // Direct download fallback
      const blob = new Blob([vCardData], { type: 'text/vcard' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${contact.name}.vcf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setError(null);
      return true;
    } catch (error) {
      console.error('Error saving to contacts:', error);
      setError('Failed to save contact. Please try downloading the vCard file.');
      return false;
    }
  };

  const handleEditQRCode = (qrCode: QRCodeData) => {
    if (qrCode.affiliate_enabled) {
      setError('The NetworkQR affiliate card cannot be modified. You can update your affiliate ID in the Profile section.');
      return;
    }
    setEditingQRCode(qrCode);
    setActiveTab('create');
  };

  const handleDeleteQRCode = async (id: string) => {
    try {
      const qrCode = qrCodes.find(qr => qr.id === id);
      
      if (qrCode?.affiliate_enabled) {
        setError('The NetworkQR affiliate card cannot be deleted.');
        return;
      }

      if (!window.confirm('Are you sure you want to delete this QR code? This action cannot be undone.')) {
        return;
      }

      setLoading(true);
      setError(null);

      const { error } = await supabase
        .from('qr_codes')
        .delete()
        .match({ 
          id: id,
          affiliate_enabled: false
        });

      if (error) throw error;

      setQrCodes(prevCodes => prevCodes.filter(code => code.id !== id));
    } catch (error) {
      console.error('Error deleting QR code:', error);
      setError('Failed to delete QR code. Please try again.');
      await loadQRCodes();
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadQR = (qrCode: QRCodeData) => {
    const link = document.createElement('a');
    link.download = `${qrCode.title.replace(/\s+/g, '-').toLowerCase()}.png`;
    link.href = qrCode.qr_data;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleTabChange = (tab: Tab) => {
    setError(null);
    setEditingQRCode(null);
    setActiveTab(tab);
    if (tab === 'qrmode') {
      setCurrentQRIndex(0);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <p>Loading...</p>
    </div>;
  }

  if (!user) {
    return <Auth onAuthSuccess={initializeApp} />;
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-100'} flex flex-col`}>
      <header className={`${darkMode ? 'bg-gray-800' : 'bg-white'} shadow sticky top-0 z-10`}>
        <div className="px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              {userSettings.logo && (
                <img src={userSettings.logo} alt="Logo" className="h-6 w-auto object-contain" />
              )}
              <h1 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {userSettings.name ? `${userSettings.name}'s NetworkQR` : 'NetworkQR'}
              </h1>
            </div>
            <div className="flex items-center space-x-3">
              {userSettings.photo && (
                <img src={userSettings.photo} alt="Profile" className="h-8 w-8 rounded-full object-cover" />
              )}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`p-2 rounded-md ${
                  darkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-700'
                }`}
              >
                {darkMode ? '🌞' : '🌙'}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-grow px-4 py-4 pb-20">
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md">
            {error}
          </div>
        )}
        {activeTab === 'profile' ? (
          <Profile
            darkMode={darkMode}
            onUpdateSettings={loadUserSettings}
          />
        ) : activeTab === 'scan' ? (
          <div className="mb-6">
            <QRScanner
              onScanSuccess={handleScanSuccess}
              onScanError={console.error}
              darkMode={darkMode}
            />
          </div>
        ) : activeTab === 'create' ? (
          <QRCodeForm
            darkMode={darkMode}
            onQRCodeCreated={loadQRCodes}
            editingQRCode={editingQRCode}
            folders={folders}
            onCancelEdit={() => {
              setEditingQRCode(null);
              setActiveTab('qrcodes');
            }}
          />
        ) : activeTab === 'scanned' ? (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
              <div className="flex flex-wrap gap-3">
                <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} p-3 rounded-lg shadow flex items-center`}>
                  <span className="text-xl mr-3">📊</span>
                  <div>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Scans</p>
                    <p className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {scanStats.total}
                    </p>
                  </div>
                </div>
                <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} p-3 rounded-lg shadow flex items-center`}>
                  <span className="text-xl mr-3">👥</span>
                  <div>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Leads</p>
                    <p className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {scanStats.leads}
                    </p>
                  </div>
                </div>
                <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} p-3 rounded-lg shadow flex items-center`}>
                  <span className="text-xl mr-3">🔗</span>
                  <div>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Links</p>
                    <p className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {scanStats.links}
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={handleExportCSV}
                className={`px-4 py-2 rounded-md ${
                  darkMode
                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Export to CSV
              </button>
            </div>

            {scannedQRCodes.map((qrCode) => (
              <div
                key={qrCode.id}
                className={`${darkMode ? 'bg-gray-800' : 'bg-white'} p-4 rounded-lg shadow`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-grow">
                    <div className="flex items-center justify-between">
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {qrCode.title}
                      </h3>
                      <button
                        onClick={() => handleDeleteScannedQR(qrCode.id)}
                        className="ml-4 text-red-600 hover:text-red-700"
                        aria-label="Delete scanned QR code"
                      >
                        🗑️
                      </button>
                    </div>
                    <span className={`inline-block mt-1 px-2 py-1 text-xs rounded ${
                      qrCode.lead_type === 'business_card'
                        ? 'bg-blue-100 text-blue-800'
                        : qrCode.lead_type === 'link'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {qrCode.lead_type === 'business_card' ? 'Lead' : 
                       qrCode.lead_type === 'link' ? 'Link' : 'Unknown'}
                    </span>
                  </div>
                </div>

                {qrCode.lead_type === 'business_card' && qrCode.interpreted_data && (
                  <div className={`mt-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {qrCode.interpreted_data.photo && (
                      <div className="mb-3">
                        <img
                          src={qrCode.interpreted_data.photo}
                          alt={qrCode.interpreted_data.name}
                          className="w-16 h-16 rounded-full object-cover border-2 border-gray-300"
                        />
                      </div>
                    )}
                    <p><strong>Name:</strong> {qrCode.interpreted_data.name}</p>
                    <p><strong>Email:</strong> {qrCode.interpreted_data.email}</p>
                    <p><strong>Phone:</strong> {qrCode.interpreted_data.phone}</p>
                    <p><strong>Company:</strong> {qrCode.interpreted_data.company}</p>
                    <p><strong>Title:</strong> {qrCode.interpreted_data.title}</p>
                    
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => handleSaveToContacts(qrCode.interpreted_data)}
                        className={`px-3 py-1 text-sm rounded-md ${
                          darkMode
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                        }`}
                      >
                        Save to Contacts
                      </button>
                      <a
                        href={`data:text/vcard;charset=utf-8,${encodeURIComponent(createVCard(qrCode.interpreted_data))}`}
                        download={`${qrCode.interpreted_data.name}.vcf`}
                        className={`px-3 py-1 text-sm rounded-md ${
                          darkMode
                            ? 'bg-gray-700 text-white hover:bg-gray-600'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        Download VCF
                      </a>
                    </div>
                  </div>
                )}

                <p className={`text-sm break-words ${darkMode ? 'text-gray-300' : 'text-gray-600'} mt-2`}>
                  {qrCode.raw_data}
                </p>
                <p className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Scanned: {new Date(qrCode.scanned_at).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        ) : activeTab === 'qrmode' ? (
          <div>
            <FolderManager
              darkMode={darkMode}
              onFolderCreated={loadFolders}
              folders={folders}
              selectedFolder={selectedFolder}
              onFolderSelect={setSelectedFolder}
            />
            <div className="overflow-x-hidden relative">
              <div
                ref={carouselRef}
                className="flex snap-x snap-mandatory overflow-x-auto no-scrollbar smooth-scroll px-[5%] sm:px-[20%]"
              >
              {filteredQRCodes.map((qrCode, index) => (
                <div
                  key={qrCode.id}
                  data-index={index}
                  className="qr-card flex-shrink-0 w-[90%] sm:w-[60%] snap-center px-2"
                >
                  <div 
                    className={`
                      ${darkMode ? 'bg-gray-800' : 'bg-white'} 
                      p-6 rounded-lg shadow-lg mx-auto 
                      transform transition-all duration-300 ease-in-out
                      ${index === currentQRIndex ? 'sm:scale-110 scale-100 z-10 opacity-100' : 
                        Math.abs(index - currentQRIndex) === 1 ? 'sm:scale-95 scale-90 opacity-70' : 
                        'sm:scale-90 scale-90 opacity-40'}
                    `}
                  >
                    <h3 className={`text-xl font-bold text-center mb-4 ${darkMode ? 'text-white' : 'text-gray-900'} 
                      ${index !== currentQRIndex && 'opacity-75'}`}
                    >
                      {qrCode.title}
                    </h3>
                    <img
                      src={qrCode.qr_data}
                      alt={`QR code for ${qrCode.title}`}
                      className={`w-full mb-4 transition-transform duration-300 
                        ${index === currentQRIndex ? 'transform-none' : 'blur-[0.5px]'}`}
                    />
                    {qrCode.is_business_card && qrCode.contact_info && (
                      <div className={`text-center text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} 
                        ${index !== currentQRIndex && 'opacity-75'}`}
                      >
                        <p className="font-semibold">{qrCode.contact_info.name}</p>
                        <p>{qrCode.contact_info.title}</p>
                        <p>{qrCode.contact_info.company}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              </div>
              <div className={`absolute inset-y-0 left-0 w-[5%] sm:w-[20%] bg-gradient-to-r from-${darkMode ? 'gray-900' : 'gray-100'} to-transparent pointer-events-none`} />
              <div className={`absolute inset-y-0 right-0 w-[5%] sm:w-[20%] bg-gradient-to-l from-${darkMode ? 'gray-900' : 'gray-100'} to-transparent pointer-events-none`} />
            </div>
          </div>
        ) : (
          <div>
            <FolderManager
              darkMode={darkMode}
              onFolderCreated={loadFolders}
              folders={folders}
              selectedFolder={selectedFolder}
              onFolderSelect={setSelectedFolder}
            />
            <div className="overflow-x-auto">
              <div className="flex space-x-4 snap-x snap-mandatory pb-4 -mx-4 px-4">
                {filteredQRCodes.map((qrCode) => (
                <div
                  key={qrCode.id}
                  className={`${
                    darkMode ? 'bg-gray-800' : 'bg-white'
                  } p-4 rounded-lg shadow flex-shrink-0 w-[280px] sm:w-64 snap-center`}
                >
                  <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {qrCode.title}
                  </h3>
                  <img
                    src={qrCode.qr_data}
                    alt={`QR code for ${qrCode.title}`}
                    className="w-full mb-4"
                  />
                  <div className="flex justify-between">
                    {!qrCode.affiliate_enabled ? (
                      <>
                        <button
                          onClick={() => handleEditQRCode(qrCode)}
                          className="text-blue-600 hover:text-blue-700"
                          title="Edit QR code"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteQRCode(qrCode.id)}
                          className="text-red-600 hover:text-red-700"
                          title="Delete QR code"
                        >
                          Delete
                        </button>
                      </>
                    ) : (
                      <div className="text-sm text-gray-500 italic">
                        NetworkQR affiliate card (cannot be deleted)
                      </div>
                    )}
                    <button
                      onClick={() => handleDownloadQR(qrCode)}
                      className={`text-green-600 hover:text-green-700 ${!qrCode.affiliate_enabled ? '' : 'ml-auto'}`}
                      title="Download QR code"
                    >
                      Download
                    </button>
                  </div>
                </div>
              ))}
              </div>
            </div>
          </div>
        )}
      </main>

      <nav className={`fixed bottom-0 left-0 right-0 ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg z-20`}>
        <div className="container mx-auto px-1">
          <div className="grid grid-cols-6 h-14 max-w-3xl mx-auto">
            <button
              onClick={() => handleTabChange('qrcodes')}
              className={`flex flex-col items-center justify-center h-full ${
                activeTab === 'qrcodes'
                  ? `${darkMode ? 'text-blue-400' : 'text-blue-600'}`
                  : darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}
            >
              <span className="text-base mb-0.5">📝</span>
              <span className="text-[9px] leading-none">Edit QRs</span>
            </button>
            <button
              onClick={() => handleTabChange('qrmode')}
              className={`flex flex-col items-center justify-center h-full ${
                activeTab === 'qrmode'
                  ? `${darkMode ? 'text-blue-400' : 'text-blue-600'}`
                  : darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}
            >
              <span className="text-base mb-0.5">🎯</span>
              <span className="text-[9px] leading-none">QR Mode</span>
            </button>
            <button
              onClick={() => handleTabChange('create')}
              className={`flex flex-col items-center justify-center h-full ${
                activeTab === 'create'
                  ? `${darkMode ? 'text-blue-400' : 'text-blue-600'}`
                  : darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}
            >
              <span className="text-base mb-0.5">➕</span>
              <span className="text-[9px] leading-none">Create</span>
            </button>
            <button
              onClick={() => handleTabChange('scan')}
              className={`flex flex-col items-center justify-center h-full ${
                activeTab === 'scan'
                  ? `${darkMode ? 'text-blue-400' : 'text-blue-600'}`
                  : darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}
            >
              <span className="text-base mb-0.5">📷</span>
              <span className="text-[9px] leading-none">Scan</span>
            </button>
            <button
              onClick={() => handleTabChange('scanned')}
              className={`flex flex-col items-center justify-center h-full ${
                activeTab === 'scanned'
                  ? `${darkMode ? 'text-blue-400' : 'text-blue-600'}`
                  : darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}
            >
              <span className="text-base mb-0.5">📱</span>
              <span className="text-[9px] leading-none">Scanned</span>
            </button>
            <button
              onClick={() => handleTabChange('profile')}
              className={`flex flex-col items-center justify-center h-full ${
                activeTab === 'profile'
                  ? `${darkMode ? 'text-blue-400' : 'text-blue-600'}`
                  : darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}
            >
              <span className="text-base mb-0.5">👤</span>
              <span className="text-[9px] leading-none">Profile</span>
            </button>
          </div>
        </div>
      </nav>
    </div>
  );
}

export default App;